import React, { useContext, useRef } from 'react';

import { Platform, StyleSheet, TouchableHighlight, View } from 'react-native';
import DyteClient, { DytePlugin } from '@dytesdk/web-core';
import WebView from 'react-native-webview';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  plugin,
  iconPack = defaultIconPack,
}: {
  meeting: DyteClient;
  plugin: DytePlugin;
  iconPack?: IconPack;
}) {
  if (!plugin) return null;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const webviewRef = useRef<WebView>();
  const createSetupIframe = (newSrc) => {
    if (webviewRef.current) {
      const jsCode = `
        var dyteIframe = document.createElement('iframe');
        dyteIframe.id = 'myIframe';
        dyteIframe.src = '${newSrc}';
        dyteIframe.style.position = 'fixed';
        dyteIframe.style.top = '0';
        dyteIframe.style.left = '0';
        dyteIframe.style.width = '100%';
        dyteIframe.style.height = '100%';
        dyteIframe.style.border = 'none';
        dyteIframe.style.zIndex = '999999999';
        document.body.appendChild(dyteIframe);
        ${
          Platform.OS === 'android'
            ? `
        document.addEventListener('message', (e) => {
          dyteIframe.contentWindow.postMessage(JSON.parse(e.data), '*');
        });

        window.addEventListener('message', (e) => {
          window.ReactNativeWebView.postMessage(JSON.stringify(e.data));
        });
        `
            : `
        window.addEventListener('message', (e) => {
          window.ReactNativeWebView.postMessage(JSON.stringify(e.data));
          dyteIframe.contentWindow.postMessage(JSON.parse(e.data), '*');
        });
        `
        }
      `;
      webviewRef.current.injectJavaScript(jsCode);
    }
  };
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      width: '95%',
    },
    button: {
      // right: 4,
      padding: 8,
      borderRadius: 12,
      // marginTop: 12,
      backgroundColor: colors.brand[500],
    },
    buttonContainer: {
      flexDirection: 'row',
      height: 40,
      backgroundColor: colors.background[900],
      // marginTop: 8,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    },
    buttonPosition: {
      position: 'absolute',
      top: 8,
      right: 8,
    },
    webView: {
      flex: 1,
      width: '100%',
    },
    fullscreenButton: {
      position: 'absolute',
      zIndex: 1,
      left: 8,
      top: 4,
      borderRadius: 4,
      backgroundColor: colors.background[800],
    },
  });
  return (
    <View style={styles.container}>
      <View style={styles.buttonContainer} />
      <View style={styles.fullscreenButton}>
        <TouchableHighlight
          onPress={() => {
            setStates({
              ...storeStates,
              activeFullscreen: !storeStates.activeFullscreen,
            });
          }}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            padding: 6,
          }}
        >
          <SvgXml
            xml={(storeStates.activeFullscreen
              ? iconPack.full_screen_minimize
              : iconPack.full_screen_maximize
            ).replace('currentColor', colors.text)}
            width={20}
            height={20}
          />
        </TouchableHighlight>
      </View>
      {meeting.self.permissions.plugins.canClose && (
        <View style={styles.buttonPosition}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            style={styles.button}
            onPress={() => {
              plugin.deactivate();
            }}
          >
            <SvgXml
              xml={iconPack.dismiss.replace('currentColor', colors.text)}
              height={10}
              width={10}
            />
          </TouchableHighlight>
        </View>
      )}
      <View style={styles.webView}>
        <WebView
          ref={webviewRef}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
          originWhitelist={['*']}
          source={{ uri: plugin.baseURL }}
          onLoad={() => {
            if (webviewRef.current !== null) {
              let iframeData = {
                src: plugin.baseURL,
                allow: 'true',
                title: plugin.name,
                props: {
                  onMessage: (_e) => {},
                },
                postMessage: (_data) => {
                  if (webviewRef.current) {
                    (webviewRef.current as WebView).postMessage(_data);
                  }
                },
              };
              plugin.addPluginView(iframeData);
              if (plugin.iframes) {
                (plugin.iframes as Map<string, any>).forEach((_value, _key) => {
                  if (_value.iframe.src && _value) {
                    createSetupIframe(_value.iframe.src);
                  }
                });
              }
            }
          }}
          onMessage={(event) => {
            if (plugin.iframes)
              (plugin.iframes as Map<string, any>).forEach((_value, _key) => {
                if (_value.iframe.src && event.nativeEvent.data) {
                  if (event.nativeEvent.data.length > 0) {
                    _value.iframe.props.onMessage(event);
                  }
                }
              });
          }}
          domStorageEnabled={true}
          javaScriptEnabled={true}
        />
      </View>
    </View>
  );
}
